import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mic, Award, Building, AlertTriangle } from 'lucide-react';
import { getAuthHeaders } from '../../../contexts/AuthContext';
import { API } from '../execShared';

// Icons + accent colors per engagement type (moved here from the old
// monolithic ExecutiveDashboard.js during refactor).
const ENG_TYPE_ICONS = { appearance: Mic, endorsement: Award, activation: Building };
const ENG_TYPE_COLORS = { appearance: 'text-blue-400 bg-blue-400/10', endorsement: 'text-yellow-400 bg-yellow-400/10', activation: 'text-[#10B981] bg-[#10B981]/10' };


export default function EngagementsSection() {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      const { data } = await axios.get(`${API}/executive/engagements`, { headers: getAuthHeaders() });
      setEngagements(data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const handleAccept = async (partId) => {
    try {
      await axios.post(`${API}/engagement-participants/${partId}/accept`, {}, { headers: getAuthHeaders() });
      fetch();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDecline = async (partId) => {
    if (!window.confirm('Decline this engagement?')) return;
    try {
      await axios.post(`${API}/engagement-participants/${partId}/decline`, {}, { headers: getAuthHeaders() });
      fetch();
    } catch {}
  };

  const statusConfig = {
    invited: { color: 'text-[#FF4343]', label: 'Invited' },
    accepted: { color: 'text-[#10B981]', label: 'Accepted' },
    confirmed: { color: 'text-[#10B981]', label: 'Confirmed' },
    briefed: { color: 'text-blue-400', label: 'Briefed' },
    completed: { color: 'text-[#10B981]', label: 'Completed' },
    declined: { color: 'text-[#6B7280]', label: 'Declined' },
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading engagements...</p>;

  return (
    <div className="space-y-4" data-testid="exec-engagements-section">
      <h2 className="font-display text-2xl font-bold">My <span className="text-[#FF4343]">Engagements</span></h2>
      {engagements.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <Mic className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF] text-sm">No engagement invitations yet.</p>
          <p className="text-[10px] text-[#6B7280] mt-1">When brands invite you to appearances, endorsements, or activations, they'll appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {engagements.map(p => {
            const eng = p.engagement || {};
            const Icon = ENG_TYPE_ICONS[eng.engagement_type] || Mic;
            const typeColor = ENG_TYPE_COLORS[eng.engagement_type] || 'text-[#6B7280] bg-white/5';
            const sc = statusConfig[p.status] || { color: 'text-[#6B7280]', label: p.status };
            return (
              <div key={p.id} className="bg-[#111827] border border-white/10 p-5" data-testid={`eng-part-${p.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${typeColor}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-white">{eng.title || 'Engagement'}</h3>
                      <p className="text-[10px] text-[#6B7280] mt-0.5">
                        {eng.brand_name} · <span className="capitalize">{(eng.engagement_subtype || '').replace(/_/g,' ')}</span>
                        {eng.event_date && <span> · {eng.event_date}</span>}
                      </p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono uppercase ${sc.color}`}>{sc.label}</span>
                </div>

                {/* Details */}
                {eng.description && <p className="text-xs text-[#9CA3AF] mb-3">{eng.description}</p>}

                <div className="flex flex-wrap gap-3 text-[10px] text-[#6B7280] mb-3">
                  {eng.event_location_type && <span className="capitalize">{eng.event_location_type}</span>}
                  {eng.event_location && <span>{eng.event_location}</span>}
                  {eng.event_duration_minutes && <span>{eng.event_duration_minutes} min</span>}
                  {eng.base_fee && <span className="font-mono text-[#FF4343]">${eng.base_fee}</span>}
                  {eng.compensation_type === 'in_kind' && eng.inkind_description && <span>In-kind: {eng.inkind_description}</span>}
                </div>

                {/* Deliverables */}
                {eng.deliverables?.length > 0 && (
                  <div className="mb-3">
                    <span className="text-[9px] font-mono uppercase text-[#6B7280]">Deliverables:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {eng.deliverables.map((d, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 bg-white/5 text-[#9CA3AF] border border-white/10">{d}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Exclusivity warning */}
                {eng.exclusivity_required && (
                  <div className="bg-yellow-400/5 border border-yellow-400/10 p-2 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
                    <span className="text-[10px] text-yellow-400">Exclusivity required: {eng.exclusivity_window_days} days, {eng.competitor_category} category</span>
                  </div>
                )}

                {/* Actions */}
                {p.status === 'invited' && (
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => handleAccept(p.id)} data-testid={`accept-eng-${p.id}`}
                      className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                      Accept Engagement
                    </button>
                    <button onClick={() => handleDecline(p.id)} data-testid={`decline-eng-${p.id}`}
                      className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">
                      Decline
                    </button>
                  </div>
                )}

                {(p.status === 'completed' && p.payout_amount) && (
                  <div className="bg-[#090E33] p-3 mt-2">
                    <span className="font-mono text-lg text-[#FF4343]">${p.payout_amount?.toLocaleString()}</span>
                    <span className="text-[10px] text-[#6B7280] ml-2">{p.payout_status}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== AVAILABILITY SECTION ====================
